import { Dialog } from "@fluentui/react-components";
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import configs from "../../configs";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import { resolveEditorPricingUrl } from "../../utils/editorAppConfig.js";

export const FluentDialogContext = React.createContext();

export const DialogContextProvider = ({ children, api }) => {
  const [dialog, setDialog] = useState(null);

  const showDialog = useCallback((DialogComponent, dialogProps = {}) => {
    setDialog({ DialogComponent, dialogProps });
  }, []);

  const hideDialog = useCallback(() => {
    setDialog(null);
  }, []);

  // // Block body scroll when dialog is open
  // useEffect(() => {
  //   if (dialog) {
  //     // Save current scroll position
  //     const scrollY = window.scrollY;

  //     // Block scroll
  //     document.body.style.overflow = "hidden";
  //     document.body.style.position = "fixed";
  //     document.body.style.top = `-${scrollY}px`;
  //     document.body.style.width = "100%";

  //     return () => {
  //       // Restore scroll
  //       document.body.style.overflow = "";
  //       document.body.style.position = "";
  //       document.body.style.top = "";
  //       document.body.style.width = "";

  //       // Restore scroll position
  //       window.scrollTo(0, scrollY);
  //     };
  //   }
  // }, [dialog]);

  useEffect(() => {
    api.addListener("show-dialog", ({ component, props }) => {
      setDialog({ DialogComponent: component, dialogProps: props });
    });

    return () => {
      api.removeListener("show-dialog", ({ component, props }) => {
        setDialog({ DialogComponent: component, dialogProps: props });
      });
    };
  }, [api]);

  const showLimitReachedDialog = useCallback(
    ({
      title = "Subscription Limit Reached",
      message = "You've reached your storage limit. Upgrade now to enjoy more space and uninterrupted access to your files.",
      memberMessage = null
    } = {}) => {
      const isWorkspaceOwner = api.isCurrentWorkspaceOwner();
      const fallbackMemberMessage = title.toLowerCase().includes("storage")
        ? "The workspace owner is out of storage space. Please contact them to upgrade."
        : "The workspace owner has reached the maximum number of Verses. Please contact them to upgrade.";
      const resolvedMemberMessage = memberMessage || fallbackMemberMessage;

      setDialog({
        DialogComponent: ConfirmDialog,
        dialogProps: {
          title,
          message: isWorkspaceOwner ? message : resolvedMemberMessage,
          confirmLabel: isWorkspaceOwner ? "Upgrade now" : undefined,
          confirmHref: isWorkspaceOwner
            ? resolveEditorPricingUrl({
                pricingPageUrl: configs.PRICING_PAGE_URL,
                xrhubUrl: configs.XRHUB_URL,
                locationOrigin: window.location.origin
              })
            : undefined,
          onCancel: () => {
            hideDialog();
          },
          cancelLabel: "I understand"
        }
      });
    },
    [hideDialog, api]
  );

  const handle402Error = useCallback(() => {
    showLimitReachedDialog({
      title: "Subscription Limit Reached",
      message:
        "You've reached your storage limit. Upgrade now to enjoy more space and uninterrupted access to your files."
    });
  }, [showLimitReachedDialog]);

  const handle402SceneError = useCallback(() => {
    showLimitReachedDialog({
      title: "Subscription Limit Reached",
      message:
        "You have reached the maximum number of scenes you can publish. Please upgrade your subscription to publish more scenes."
    });
  }, [showLimitReachedDialog]);

  useEffect(() => {
    api.addListener("402-error", handle402Error);
    api.addListener("402-scene-error", handle402SceneError);

    return () => {
      api.removeListener("402-error", handle402Error);
      api.removeListener("402-scene-error", handle402SceneError);
    };
  }, [api, handle402Error, handle402SceneError]);

  return (
    <FluentDialogContext.Provider value={{ showDialog, hideDialog, showLimitReachedDialog }}>
      {children}
      {dialog && dialog.DialogComponent && (
        <Dialog open={true} onDismiss={hideDialog}>
          <dialog.DialogComponent {...dialog.dialogProps} onClose={hideDialog} />
        </Dialog>
      )}
    </FluentDialogContext.Provider>
  );
};

export function useDialog() {
  const context = React.useContext(FluentDialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a FluentDialogContextProvider");
  }
  return context;
}

DialogContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  api: PropTypes.object.isRequired
};
